import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@xemplo/auth-provider';
import { CommonNavigate } from '@xemplo/common-routes';
import { safeCallWithSentryAsync } from '@xemplo/safe-call';
import { LogoutActions, XemploStateScopes } from '@xemplo/types';
import { useAppSettings } from '@xemplo/xemplo-app-settings';
import { useDeleteXemploStateMutation } from '@xemplo/xemplo-state-query';

import { LogoutProps } from '../auth-helper.types';
import {
  broadcastLogout,
  clearActiveSub,
  markLoggedOut,
} from '../helpers/auth-broadcast.helper';
import { clearSessionCookie } from '../helpers/logout.helper';

const XEMPLO_SESSION_COOKIE_NAME = 'xemploSessionId';

export function useLogout({ action }: LogoutProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { resetPersona, logoutCallback, contextReturnUrlKey } = useAppSettings();
  const remoteState = useDeleteXemploStateMutation();
  const navigate = useNavigate();

  const { isAuthenticated, isLoading, signoutCallback, signoutRedirect, user } = auth;
  const hasStartedRef = useRef(false);

  const isCallback = action === LogoutActions.LogoutCallback;

  const doCleanUp = useCallback(async () => {
    // Tell other open tabs/stacks to log out too: the broadcast covers tabs open now;
    // the marker covers ones that load later in this tab.
    broadcastLogout();
    markLoggedOut();
    // No identity is active any more; clear the marker so a later load doesn't
    // mistake the stale `sub` for a still-valid session.
    clearActiveSub();

    // If the token is already removed, this will make an unnecessary call, which will fail.
    if (user?.access_token) {
      await safeCallWithSentryAsync(remoteState.mutateAsync(XemploStateScopes.Session));
    }

    // Locally deletes the browser named cookie
    // e.g. xemploSessionId:"044c0839-c669-4d5c-8d3e-c6f51303038e"
    clearSessionCookie(XEMPLO_SESSION_COOKIE_NAME);
    sessionStorage.removeItem(contextReturnUrlKey);
    resetPersona();
    logoutCallback?.();
    queryClient.clear();
  }, [
    contextReturnUrlKey,
    logoutCallback,
    queryClient,
    remoteState,
    resetPersona,
    user?.access_token,
  ]);

  const handleCallback = useCallback(async () => {
    await doCleanUp(); // Should already be cleaned but just to be sure
    await signoutCallback();
    navigate(CommonNavigate.Login);
  }, [doCleanUp, navigate, signoutCallback]);

  const handleLogout = useCallback(async () => {
    // Handle the login callback scenario first, if applicable
    if (isCallback) return await handleCallback();

    // If the user is not authenticated, we can skip the signout
    // redirect and just do cleanup and navigate to login
    if (!isAuthenticated) {
      return await doCleanUp().then(() => navigate(CommonNavigate.Login));
    }

    // Init the logout cycle
    await doCleanUp();
    await signoutRedirect();
  }, [doCleanUp, handleCallback, isAuthenticated, isCallback, navigate, signoutRedirect]);

  useEffect(() => {
    if (isLoading || hasStartedRef.current) return;
    hasStartedRef.current = true;
    safeCallWithSentryAsync(handleLogout());
  }, [handleLogout, isLoading]);
}
